/**
 * BlockMenu Component
 * Notion-style context menu for block operations
 * 
 * Features:
 * - Move nodes up/down with keyboard shortcuts
 * - Turn into different block types
 * - Color and background customization
 * - Duplicate, copy, delete operations
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';

interface BlockMenuProps {
    editor: Editor;
    isOpen: boolean;
    position: { x: number; y: number };
    onClose: () => void;
    currentNodeType?: string;
}

interface MenuItem {
    icon: React.ReactNode;
    label: string;
    shortcut?: string;
    action?: () => void;
    submenu?: MenuItem[];
    divider?: boolean;
}

/**
 * Check if a node can be moved in the specified direction
 */
const canMoveNode = (editor: Editor | null, direction: 'up' | 'down'): boolean => {
    if (!editor) return false;

    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;

    // Get the depth of the current block
    const depth = $from.depth;
    if (depth < 1) return false;

    // Get the parent node and the position of the current node within it
    const parentNode = $from.node(depth - 1);
    const indexInParent = $from.index(depth - 1);

    if (direction === 'up') {
        // Can move up if not the first child
        return indexInParent > 0;
    } else {
        // Can move down if not the last child
        return indexInParent < parentNode.childCount - 1;
    }
};

/**
 * Move the selected node in the specified direction
 */
const moveNode = (editor: Editor | null, direction: 'up' | 'down'): boolean => {
    if (!editor) return false;
    if (!canMoveNode(editor, direction)) return false;

    const { state, view } = editor;
    const { tr } = state;
    const { selection } = state;
    const { $from } = selection;

    try {
        const depth = $from.depth;
        const parentNode = $from.node(depth - 1);
        const indexInParent = $from.index(depth - 1);

        // Get the range of the current node
        const nodeStart = $from.before(depth);
        const nodeEnd = $from.after(depth);
        const nodeContent = state.doc.slice(nodeStart, nodeEnd);

        if (direction === 'up' && indexInParent > 0) {
            // Get the position of the previous sibling
            let prevNodeStart = nodeStart;
            let offset = 0;
            for (let i = 0; i < indexInParent - 1; i++) {
                offset += parentNode.child(i).nodeSize;
            }
            prevNodeStart = $from.start(depth - 1) + offset;
            const prevNodeEnd = prevNodeStart + parentNode.child(indexInParent - 1).nodeSize;

            // Delete the current node
            tr.delete(nodeStart, nodeEnd);
            // Insert before the previous node (adjust position after deletion)
            const insertPos = prevNodeStart;
            tr.insert(insertPos, nodeContent.content);

            view.dispatch(tr);

            // Set selection to the moved node
            const newPos = insertPos + 1;
            editor.commands.setTextSelection(newPos);

            return true;
        } else if (direction === 'down' && indexInParent < parentNode.childCount - 1) {
            // Get the position of the next sibling
            const nextNodeStart = nodeEnd;
            const nextNodeEnd = nextNodeStart + parentNode.child(indexInParent + 1).nodeSize;

            // Delete the next node first
            const nextContent = state.doc.slice(nextNodeStart, nextNodeEnd);
            tr.delete(nextNodeStart, nextNodeEnd);
            // Insert the next node before current node
            tr.insert(nodeStart, nextContent.content);

            view.dispatch(tr);

            // Set selection to the moved node
            const newPos = nodeStart + nextContent.content.size + 1;
            editor.commands.setTextSelection(newPos);

            return true;
        }

        return false;
    } catch (e) {
        console.error('Error moving node:', e);
        return false;
    }
};

const BlockMenu: React.FC<BlockMenuProps> = ({
    editor,
    isOpen,
    position,
    onClose,
    currentNodeType = 'Paragraph',
}) => {
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Check if movement is possible
    const canMoveUp = canMoveNode(editor, 'up');
    const canMoveDown = canMoveNode(editor, 'down');

    // Handle move up
    const handleMoveUp = useCallback(() => {
        if (moveNode(editor, 'up')) {
            onClose();
        }
    }, [editor, onClose]);

    // Handle move down
    const handleMoveDown = useCallback(() => {
        if (moveNode(editor, 'down')) {
            onClose();
        }
    }, [editor, onClose]);

    // Register keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            // Cmd/Ctrl + Shift + Arrow Up/Down
            if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
                if (e.key === 'ArrowUp' && canMoveUp) {
                    e.preventDefault();
                    handleMoveUp();
                } else if (e.key === 'ArrowDown' && canMoveDown) {
                    e.preventDefault();
                    handleMoveDown();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, canMoveUp, canMoveDown, handleMoveUp, handleMoveDown]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const colorOptions = [
        { name: 'Default', color: '#1f2937', bg: 'transparent' },
        { name: 'Gray', color: '#6b7280', bg: 'transparent' },
        { name: 'Brown', color: '#92400e', bg: 'transparent' },
        { name: 'Red', color: '#dc2626', bg: 'transparent' },
        { name: 'Orange', color: '#ea580c', bg: 'transparent' },
        { name: 'Yellow', color: '#ca8a04', bg: 'transparent' },
        { name: 'Green', color: '#16a34a', bg: 'transparent' },
        { name: 'Blue', color: '#2563eb', bg: 'transparent' },
        { name: 'Purple', color: '#9333ea', bg: 'transparent' },
        { name: 'Pink', color: '#db2777', bg: 'transparent' },
    ];

    const bgOptions = [
        { name: 'Default', bg: 'transparent' },
        { name: 'Gray', bg: '#f3f4f6' },
        { name: 'Brown', bg: '#fef3c7' },
        { name: 'Red', bg: '#fee2e2' },
        { name: 'Orange', bg: '#ffedd5' },
        { name: 'Yellow', bg: '#fef9c3' },
        { name: 'Green', bg: '#dcfce7' },
        { name: 'Blue', bg: '#dbeafe' },
        { name: 'Purple', bg: '#f3e8ff' },
        { name: 'Pink', bg: '#fce7f3' },
    ];

    const turnIntoOptions = [
        { label: 'Text', icon: 'T', action: () => editor.chain().focus().setParagraph().run() },
        { label: 'Heading 1', icon: 'H₁', action: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
        { label: 'Heading 2', icon: 'H₂', action: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
        { label: 'Heading 3', icon: 'H₃', action: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
        { label: 'Bullet list', icon: '•', action: () => editor.chain().focus().toggleBulletList().run() },
        { label: 'Numbered list', icon: '1.', action: () => editor.chain().focus().toggleOrderedList().run() },
        { label: 'Quote', icon: '❝', action: () => editor.chain().focus().toggleBlockquote().run() },
    ];

    const handleDuplicate = () => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Get the current block node
        const node = $from.node($from.depth);
        if (node) {
            const endPos = $from.end($from.depth);
            editor.chain()
                .focus()
                .insertContentAt(endPos + 1, node.toJSON())
                .run();
        }
        onClose();
    };

    const handleCopy = () => {
        const html = editor.getHTML();
        navigator.clipboard.writeText(html);
        onClose();
    };

    const handleDelete = () => {
        editor.chain().focus().deleteNode(editor.state.selection.$from.parent.type.name).run();
        onClose();
    };

    const handleAskAI = () => {
        window.dispatchEvent(new CustomEvent('ai-ask'));
        onClose();
    };

    return (
        <div
            ref={menuRef}
            className="block-menu"
            style={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 9999,
            }}
        >
            {/* Current block type header */}
            <div className="block-menu-header">{currentNodeType}</div>

            {/* Color submenu */}
            <div
                className="block-menu-item has-submenu"
                onMouseEnter={() => setActiveSubmenu('color')}
                onMouseLeave={() => setActiveSubmenu(null)}
            >
                <span className="block-menu-icon">🎨</span>
                <span className="block-menu-label">Color</span>
                <span className="block-menu-arrow">›</span>

                {activeSubmenu === 'color' && (
                    <div className="block-submenu color-submenu">
                        <div className="submenu-section">
                            <div className="submenu-title">Color</div>
                            <div className="color-grid">
                                {colorOptions.map((opt) => (
                                    <button
                                        key={opt.name}
                                        className="color-option"
                                        title={opt.name}
                                        onClick={() => {
                                            // Apply text color
                                            onClose();
                                        }}
                                    >
                                        <span style={{ color: opt.color }}>A</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="submenu-section">
                            <div className="submenu-title">Background</div>
                            <div className="color-grid">
                                {bgOptions.map((opt) => (
                                    <button
                                        key={opt.name}
                                        className="color-option"
                                        title={opt.name}
                                        onClick={() => {
                                            editor.chain().focus().toggleHighlight({ color: opt.bg }).run();
                                            onClose();
                                        }}
                                    >
                                        <span style={{ background: opt.bg === 'transparent' ? '#fff' : opt.bg, padding: '2px 4px', borderRadius: '2px' }}>A</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Turn Into submenu */}
            <div
                className="block-menu-item has-submenu"
                onMouseEnter={() => setActiveSubmenu('turnInto')}
                onMouseLeave={() => setActiveSubmenu(null)}
            >
                <span className="block-menu-icon">↻</span>
                <span className="block-menu-label">Turn into</span>
                <span className="block-menu-arrow">›</span>

                {activeSubmenu === 'turnInto' && (
                    <div className="block-submenu">
                        {turnIntoOptions.map((opt) => (
                            <button
                                key={opt.label}
                                className="submenu-item"
                                onClick={() => {
                                    opt.action();
                                    onClose();
                                }}
                            >
                                <span className="submenu-icon">{opt.icon}</span>
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Reset formatting */}
            <button className="block-menu-item" onClick={() => { editor.chain().focus().unsetAllMarks().run(); onClose(); }}>
                <span className="block-menu-icon">⟲</span>
                <span className="block-menu-label">Reset formatting</span>
            </button>

            <div className="block-menu-divider" />

            {/* Move Up */}
            <button
                className={`block-menu-item ${!canMoveUp ? 'disabled' : ''}`}
                onClick={handleMoveUp}
                disabled={!canMoveUp}
            >
                <span className="block-menu-icon">↑</span>
                <span className="block-menu-label">Move up</span>
                <span className="block-menu-shortcut">⌘⇧↑</span>
            </button>

            {/* Move Down */}
            <button
                className={`block-menu-item ${!canMoveDown ? 'disabled' : ''}`}
                onClick={handleMoveDown}
                disabled={!canMoveDown}
            >
                <span className="block-menu-icon">↓</span>
                <span className="block-menu-label">Move down</span>
                <span className="block-menu-shortcut">⌘⇧↓</span>
            </button>

            <div className="block-menu-divider" />

            {/* Duplicate */}
            <button className="block-menu-item" onClick={handleDuplicate}>
                <span className="block-menu-icon">⧉</span>
                <span className="block-menu-label">Duplicate node</span>
                <span className="block-menu-shortcut">⌘D</span>
            </button>

            {/* Copy */}
            <button className="block-menu-item" onClick={handleCopy}>
                <span className="block-menu-icon">📋</span>
                <span className="block-menu-label">Copy to clipboard</span>
                <span className="block-menu-shortcut">⌘C</span>
            </button>

            {/* Copy anchor link */}
            <button className="block-menu-item" onClick={() => onClose()}>
                <span className="block-menu-icon">🔗</span>
                <span className="block-menu-label">Copy anchor link</span>
                <span className="block-menu-shortcut">⌘⌥L</span>
            </button>

            <div className="block-menu-divider" />

            {/* Ask AI */}
            <button className="block-menu-item" onClick={handleAskAI}>
                <span className="block-menu-icon">✦</span>
                <span className="block-menu-label">Ask AI</span>
                <span className="block-menu-shortcut">⌘J</span>
            </button>

            <div className="block-menu-divider" />

            {/* Delete */}
            <button className="block-menu-item delete" onClick={handleDelete}>
                <span className="block-menu-icon">🗑</span>
                <span className="block-menu-label">Delete</span>
                <span className="block-menu-shortcut">Del</span>
            </button>

            <style>{`
        .block-menu {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
          min-width: 240px;
          padding: 6px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .block-menu-header {
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #1f2937;
          border-bottom: 1px solid #f3f4f6;
          margin-bottom: 4px;
        }

        .block-menu-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          text-align: left;
          transition: background 0.1s ease;
          position: relative;
          gap: 10px;
        }

        .block-menu-item:hover {
          background: #f3f4f6;
        }

        .block-menu-item.delete:hover {
          background: #fee2e2;
        }

        .block-menu-item.delete .block-menu-label {
          color: #dc2626;
        }

        .block-menu-item.disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .block-menu-item.disabled:hover {
          background: transparent;
        }

        .block-menu-icon {
          width: 20px;
          font-size: 14px;
          text-align: center;
          flex-shrink: 0;
        }

        .block-menu-label {
          flex: 1;
          font-size: 13px;
          color: #374151;
        }

        .block-menu-shortcut {
          font-size: 11px;
          color: #9ca3af;
          font-family: monospace;
        }

        .block-menu-arrow {
          color: #9ca3af;
          font-size: 14px;
        }

        .block-menu-divider {
          height: 1px;
          background: #f3f4f6;
          margin: 4px 0;
        }

        .block-submenu {
          position: absolute;
          left: 100%;
          top: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
          min-width: 180px;
          padding: 6px;
          margin-left: 4px;
        }

        .color-submenu {
          min-width: 200px;
          padding: 12px;
        }

        .submenu-section {
          margin-bottom: 12px;
        }

        .submenu-section:last-child {
          margin-bottom: 0;
        }

        .submenu-title {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .color-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 4px;
        }

        .color-option {
          width: 28px;
          height: 28px;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
          transition: all 0.1s ease;
        }

        .color-option:hover {
          border-color: #6366f1;
          transform: scale(1.1);
        }

        .submenu-item {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          text-align: left;
          font-size: 13px;
          color: #374151;
          transition: background 0.1s ease;
        }

        .submenu-item:hover {
          background: #f3f4f6;
        }

        .submenu-icon {
          width: 20px;
          text-align: center;
          font-size: 14px;
        }
      `}</style>
        </div>
    );
};

export default BlockMenu;
